import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';

const TransactionHistory = ({ transactions }) => {
  const [filterType, setFilterType] = useState('all');
  const [sortBy, setSortBy] = useState('date-desc');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const transactionTypes = [
    { value: 'all', label: 'All Types' },
    { value: 'purchase', label: 'Purchase' },
    { value: 'sale', label: 'Sale' },
    { value: 'adjustment', label: 'Adjustment' },
    { value: 'transfer', label: 'Transfer' },
    { value: 'return', label: 'Return' }
  ];

  const sortOptions = [
    { value: 'date-desc', label: 'Date (Newest First)' },
    { value: 'date-asc', label: 'Date (Oldest First)' },
    { value: 'quantity-desc', label: 'Quantity (High to Low)' },
    { value: 'quantity-asc', label: 'Quantity (Low to High)' }
  ];

  const getTransactionIcon = (type) => {
    switch (type) {
      case 'purchase': return 'ShoppingCart';
      case 'sale': return 'TrendingUp';
      case 'adjustment': return 'Edit';
      case 'transfer': return 'ArrowRightLeft';
      case 'return': return 'RotateCcw';
      default: return 'Package';
    }
  };

  const getTransactionColor = (type) => {
    switch (type) {
      case 'purchase': return 'text-success bg-success/10';
      case 'sale': return 'text-primary bg-primary/10';
      case 'adjustment': return 'text-warning bg-warning/10';
      case 'transfer': return 'text-secondary bg-secondary/10';
      case 'return': return 'text-destructive bg-destructive/10';
      default: return 'text-muted-foreground bg-muted';
    }
  };

  const getQuantityDisplay = (transaction) => {
    const sign = ['purchase', 'return', 'adjustment-add']?.includes(transaction?.type) ? '+' : '-';
    return `${sign}${transaction?.quantity}`;
  };

  const getQuantityColor = (transaction) => {
    return ['purchase', 'return', 'adjustment-add']?.includes(transaction?.type) 
      ? 'text-success' :'text-destructive';
  };

  // Filter and sort transactions
  const filteredTransactions = transactions?.filter(transaction => {
      if (filterType !== 'all' && transaction?.type !== filterType) return false;
      return true;
    })?.sort((a, b) => {
      switch (sortBy) {
        case 'date-desc':
          return new Date(b.date) - new Date(a.date);
        case 'date-asc':
          return new Date(a.date) - new Date(b.date);
        case 'quantity-desc':
          return b?.quantity - a?.quantity;
        case 'quantity-asc':
          return a?.quantity - b?.quantity;
        default:
          return 0;
      }
    });

  // Pagination
  const totalPages = Math.ceil(filteredTransactions?.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedTransactions = filteredTransactions?.slice(startIndex, startIndex + itemsPerPage);

  const handleExport = () => {
    const csvContent = [
      ['Date', 'Type', 'Quantity', 'Location', 'User', 'Reference', 'Notes'],
      ...filteredTransactions?.map(t => [
        new Date(t.date)?.toLocaleDateString(),
        t?.type,
        getQuantityDisplay(t),
        t?.location,
        t?.user,
        t?.reference || '',
        t?.notes || ''
      ])
    ]?.map(row => row?.join(','))?.join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL?.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'transaction-history.csv';
    a?.click();
    window.URL?.revokeObjectURL(url);
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6 shadow-card">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
            <Icon name="History" size={20} className="text-primary" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-foreground">Transaction History</h2>
            <p className="text-sm text-muted-foreground">Complete inventory movement log</p>
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          iconName="Download"
          iconPosition="left"
          onClick={handleExport}
        >
          Export CSV
        </Button>
      </div>
      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <Select
          label="Transaction Type"
          options={transactionTypes}
          value={filterType}
          onChange={setFilterType}
        />
        <Select
          label="Sort By"
          options={sortOptions}
          value={sortBy}
          onChange={setSortBy}
        />
      </div>
      {/* Transaction List */}
      <div className="space-y-3 mb-6">
        {paginatedTransactions?.length > 0 ? (
          paginatedTransactions?.map((transaction) => (
            <div key={transaction?.id} className="border border-border rounded-lg p-4 hover:bg-muted/30 transition-hover">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${getTransactionColor(transaction?.type)}`}>
                    <Icon name={getTransactionIcon(transaction?.type)} size={16} />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-foreground capitalize">{transaction?.type}</span>
                      <span className={`font-semibold ${getQuantityColor(transaction)}`}>
                        {getQuantityDisplay(transaction)}
                      </span>
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {new Date(transaction.date)?.toLocaleDateString()} at {new Date(transaction.date)?.toLocaleTimeString()}
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-sm font-medium text-foreground">{transaction?.location}</div>
                  <div className="text-xs text-muted-foreground">by {transaction?.user}</div>
                </div>
              </div>

              {transaction?.reference && (
                <div className="text-sm text-muted-foreground mb-1">
                  <span className="font-medium">Reference:</span> {transaction?.reference}
                </div>
              )}

              {transaction?.notes && (
                <div className="text-sm text-muted-foreground">
                  <span className="font-medium">Notes:</span> {transaction?.notes}
                </div>
              )}

              {transaction?.balanceAfter !== undefined && (
                <div className="text-xs text-muted-foreground mt-2 pt-2 border-t border-border">
                  Balance after transaction: <span className="font-medium text-foreground">{transaction?.balanceAfter} units</span>
                </div>
              )}
            </div>
          ))
        ) : (
          <div className="text-center py-8">
            <Icon name="Search" size={48} className="text-muted-foreground mx-auto mb-3" />
            <p className="text-muted-foreground">No transactions found matching your filters</p>
          </div>
        )}
      </div>
      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-muted-foreground">
            Showing {startIndex + 1}-{Math.min(startIndex + itemsPerPage, filteredTransactions?.length)} of {filteredTransactions?.length} transactions
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              iconName="ChevronLeft"
              disabled={currentPage === 1}
              onClick={() => setCurrentPage(currentPage - 1)}
            >
              Previous
            </Button>
            <div className="flex items-center gap-1">
              {Array.from({ length: totalPages }, (_, i) => i + 1)?.map((page) => (
                <Button
                  key={page}
                  variant={currentPage === page ? "default" : "ghost"}
                  size="sm"
                  onClick={() => setCurrentPage(page)}
                  className="w-8 h-8"
                >
                  {page}
                </Button>
              ))}
            </div>
            <Button
              variant="outline"
              size="sm"
              iconName="ChevronRight"
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage(currentPage + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TransactionHistory;